import type { Account, AccountFilters, AccountServiceResponse, MarketplaceType } from '../types'
import type { IAccountService } from './IAccountService'
import * as AccountService from "src/db/serviceAccount"
import * as lc from "src/app/modules/localstorage/index"

class AccountServiceClass implements IAccountService {
  private static instance: AccountServiceClass
  private cache: Map<string, { data: Account[]; timestamp: number }> = new Map()
  private readonly CACHE_TTL = 30 * 60 * 1000 // 30 minutes in milliseconds

  static getInstance(): AccountServiceClass {
    if (!this.instance) {
      this.instance = new AccountServiceClass()
    }
    return this.instance
  }

  /**
   * Fetch accessible accounts/stores from the server
   * This is the main method that replaces fetchAccessibleStores from ProductListingSlice
   */
  async getAccessibleAccounts(useCache = true): Promise<Account[]> {
    const cacheKey = 'accessible_accounts'
    
    // Check cache first if enabled
    if (useCache && this.isCacheValid(cacheKey)) {
      const cached = this.cache.get(cacheKey)
      if (cached) {
        return cached.data
      }
    }

    try {
      // Get authentication data from local storage
      const client = lc.getItemLC(lc.LCName.Client)
      const companyAccess = lc.getItemLC(lc.LCName.CompanyList)
      
      if (!client?.id || !companyAccess) {
        throw new Error('Client or company access not found in local storage')
      }

      // Fetch accounts from server
      const accounts = await AccountService.getAccessibleAccount(client.id, companyAccess)
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No accessible accounts found')
      }

      // Transform server response to our Account format
      const transformedAccounts: Account[] = accounts.map(account => ({
        id: account.id,
        name: this.formatAccountName(account),
        marketplace: this.normalizeMarketplaceType(account.type),
        type: account.type || 'unknown',
        isActive: account.isActive !== false, // Default to true if not specified
        createdAt: account.createdAt,
        updatedAt: account.updatedAt,
        logo: undefined, // Will be handled by UI components based on marketplace
        url: account.url
      }))

      // Cache the results
      if (useCache) {
        this.cache.set(cacheKey, {
          data: transformedAccounts,
          timestamp: Date.now()
        })
      }

      return transformedAccounts
    } catch (error) {
      console.error('Error fetching accessible accounts:', error)
      throw error
    }
  }

  /**
   * Get filtered accounts based on criteria
   */
  async getFilteredAccounts(filters: AccountFilters = {}, useCache = true): Promise<Account[]> {
    const allAccounts = await this.getAccessibleAccounts(useCache)
    
    return allAccounts.filter(account => {
      // Marketplace filter
      if (filters.marketplace && account.marketplace !== filters.marketplace) {
        return false
      }

      // Active status filter
      if (filters.isActive !== undefined && account.isActive !== filters.isActive) {
        return false
      }

      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        return account.name.toLowerCase().includes(searchLower) ||
               account.marketplace.toLowerCase().includes(searchLower)
      }

      return true
    })
  }

  /**
   * Get a single account by ID
   */
  async getAccountById(accountId: string, useCache = true): Promise<Account | null> {
    const accounts = await this.getAccessibleAccounts(useCache)
    return accounts.find(account => account.id === accountId) || null
  }

  /**
   * Refresh accounts cache
   */
  async refreshAccounts(): Promise<Account[]> {
    this.clearCache()
    return this.getAccessibleAccounts(false)
  }

  /**
   * Clear the accounts cache
   */
  clearCache(): void {
    this.cache.clear()
  }

  /**
   * Check if cache is still valid for a given key
   */
  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key)
    if (!cached) return false
    
    const now = Date.now()
    return (now - cached.timestamp) < this.CACHE_TTL
  }

  /**
   * Format account name for display
   * Example: "Shopee - My Store Name"
   */
  private formatAccountName(account: any): string {
    const marketplaceType = account.type || 'unknown'
    const formattedMarketplace = marketplaceType.charAt(0).toUpperCase() + marketplaceType.slice(1).toLowerCase()
    const accountName = account.name || 'Store'
    
    return `${formattedMarketplace} - ${accountName}`
  }

  /**
   * Normalize marketplace type to our enum
   */
  private normalizeMarketplaceType(type: string): MarketplaceType {
    if (!type) return 'unknown'
    
    const normalized = type.toLowerCase()
    
    // Handle common variations
    switch (normalized) {
      case 'tiktokshop':
      case 'tiktok_shop':
        return 'tiktokshop'
      case 'tiktok':
        return 'tiktok'
      case 'shopee':
        return 'shopee'
      case 'lazada':
        return 'lazada'
      case 'tokopedia':
        return 'tokopedia'
      case 'blibli':
        return 'blibli'
      case 'bukalapak':
        return 'bukalapak'
      case 'jdid':
        return 'jdid'
      case 'bhinneka':
        return 'bhinneka'
      case 'orami':
        return 'orami'
      default:
        return 'unknown'
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { keys: string[]; totalSize: number; oldestEntry: number | null } {
    const keys = Array.from(this.cache.keys())
    const totalSize = this.cache.size
    let oldestEntry: number | null = null
    
    this.cache.forEach(entry => {
      if (oldestEntry === null || entry.timestamp < oldestEntry) {
        oldestEntry = entry.timestamp
      }
    })
    
    return { keys, totalSize, oldestEntry }
  }
}

// Export singleton instance
export default AccountServiceClass